import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, Button, CircularProgress, TextField, Autocomplete } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleIcon from "@mui/icons-material/People";
import { getBookingDetails, cancelBooking } from "../api/actions/query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

const CancelBooking = ({ bookingId }) => {
    const [bookingDetails, setBookingDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCancelled, setIsCancelled] = useState(false);
    const [selectedReason, setSelectedReason] = useState("");
    const [cancelLoading, setCancelLoading] = useState(false);

    const predefinedReasons = [ "Change of plans", "Booked by mistake", "Found another option", "Health Reasons" ];

    useEffect(() => {
        if (!bookingId) {
            setError("Invalid booking ID.");
            setLoading(false);
            return;
        }

        const fetchBookingDetails = async () => {
            try {
                const response = await getBookingDetails(bookingId);
                const bookingData = response.data.data;
                setBookingDetails(bookingData);
                if (bookingData.status === "canceled") {
                    setIsCancelled(true);
                }
                
            } catch (e) {
                console.error("Error fetching booking details: ", e)
                setError("Failed to load booking details.");
            } finally {
                setLoading(false);
            }
        };

        fetchBookingDetails();
    }, [bookingId]);  

    const handleCancelBooking = async () => {
        if (cancelLoading || isCancelled) return;
        const reason = selectedReason.trim();
        if (!reason) {
            alert("Please select or provide a reason for cancellation.");
            return;
        }
        const confirmCancel = window.confirm("Are you sure you want to cancel your booking?");
        if (confirmCancel) {
            try {
                setCancelLoading(true);
                await cancelBooking(bookingId, reason);
                setBookingDetails(null);
                setIsCancelled(true);
                setSelectedReason("");
            } catch (e) {
                console.error("Error cancelling Booking: ", e);
                alert("Failed to cancel booking. Please try again.");
            }  finally {
               setCancelLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress size={60} />
            </div>
        );
    }
    if (error) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                {error}
            </div>
        )
    }
    
    const formatDateTime = (dateTime) => {
      const d = dayjs(dateTime).tz(dayjs.tz.guess());
      return {
        date: d.format("dddd, DD MMMM YYYY"),
        time: d.format("hh:mm A"),
      };
    };

    const formattedTimeSlot = bookingDetails ? formatDateTime(bookingDetails.timeSlot) : null;

    return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
            <Card style={{ width: "420px", backgroundColor: "white", borderRadius: "10px", boxShadow: "0px 0px 10px rgba(0,0,0,0.1)", padding: '30px' }}>
                <CardContent>
                    {isCancelled ? (
                        <Typography variant="h6" textAlign="center">
                            Your reservation has been cancelled.
                        </Typography>
                    ) : bookingDetails ? (
                        <>
                            <Typography variant="h6" textAlign="center" mb={4}>
                                Your reservation is confirmed, and we look forward to welcoming you!
                            </Typography>
                            <Typography variant="body1" display="flex" alignItems="center" gap="8px" mb='10px'>
                                <CalendarMonthIcon color="inherit" /> {formattedTimeSlot.date}
                            </Typography>
                            <Typography variant="body1" display="flex" alignItems="center" gap="8px" mb='10px'>
                                <AccessTimeIcon color="inherit" /> {formattedTimeSlot.time}
                            </Typography>
                            <Typography variant="body1" display="flex" alignItems="center" gap="8px">
                                <PeopleIcon color="inherit" /> {bookingDetails.name}
                            </Typography>
                            <Typography variant="body1" sx={{ marginLeft: "32px" }}>
                                Party of {bookingDetails.persons}
                            </Typography>

                            <Typography variant="subtitle1" mt={3}>
                                Please provide a reason for cancellation:
                            </Typography>
                            <Autocomplete
                                freeSolo
                                options={predefinedReasons}
                                value={selectedReason}
                                onChange={(e, newValue) => setSelectedReason(newValue || "")}
                                onInputChange={(e, newInputValue) => setSelectedReason(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Choose or Enter Reason"
                                        fullWidth
                                        margin="normal"
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: {
                                                height: 50,
                                                fontSize: '16px',
                                            },
                                        }}
                                        
                                    />
                                )}
                            />
                            <Button
                                variant="contained"
                                color="error"
                                fullWidth
                                onClick={handleCancelBooking}
                                disabled={cancelLoading}
                                style={{
                                    marginTop: "35px",
                                    border: "2px solid #d32f2f",
                                    color: "#d32f2f",
                                    backgroundColor: "transparent",
                                    fontWeight: "bold",
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    padding: "5px 0",
                                    fontSize: "16px",
                                    transition: "background-color 0.3s, color 0.3s",
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.backgroundColor = "#d32f2f";
                                    e.target.style.color = "white";
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.backgroundColor = "transparent";
                                    e.target.style.color = "#d32f2f";
                                }}
                            >
                                {cancelLoading ? "Cancelling..." : "Cancel Reservation"}
                            </Button>
                        </>
                    ) : (
                        <Typography>No active booking found.</Typography>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default CancelBooking;
